'use memo';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { match } from 'ts-pattern';

import { NotFound } from '@/components/Error/NotFound';
import { HStack, Spacer } from '@/components/Layout';
import { WorkerType } from '@/constants/workerType';
import {
  AutomapIngesterContext,
  CompletionStep,
  IngesterStepperSideNav,
  ReviewStep,
  UploadStep,
  automapIngesterSelectors,
  useAutomapIngester,
} from '@/features/automap-ingester';
import { featureFlags } from '@/lib/env';

import { PEOPLE_SEED } from './-constants/peopleSeed';
import useWorkerTypeParam from './-hooks/useWorkerType';

export const Route = createFileRoute(
  '/_staff/person/automap-ingester/$workerType/',
)({
  component: Gate,
});

function Gate() {
  if (!featureFlags.check('LABOR_PLUS_MOCK')) {
    return <NotFound />;
  }
  return <Providered />;
}

function Providered() {
  const store = AutomapIngesterContext.createStore();
  return (
    <AutomapIngesterContext.Provider value={store}>
      <AutomapIngesterPage />
    </AutomapIngesterContext.Provider>
  );
}

function AutomapIngesterPage() {
  const navigate = useNavigate();
  const { workerType } = useWorkerTypeParam();
  const step = useAutomapIngester(automapIngesterSelectors.step);

  const workerTypeLabel = match(workerType)
    .with(WorkerType.EMPLOYEE, () => '従業員')
    .with(WorkerType.CONTRACTOR, () => '業務委託')
    .with(WorkerType.BOARD_MEMBER, () => '役員')
    .with(WorkerType.COLLABORATOR, () => 'コラボレーター')
    .exhaustive();

  return (
    <HStack className="w-full h-full p-6">
      <IngesterStepperSideNav
        className="sticky top-6"
        title="Automap Ingester"
        current={step}
        labels={{
          upload: 'アップロード',
          review: '抽出結果の確認',
          complete: '取り込み結果',
        }}
      />
      <Spacer variant="m" />
      <div className="grow min-w-[340px] flex flex-col min-h-0">
        {match(step)
          .with('upload', () => (
            <UploadStep
              mockPeople={PEOPLE_SEED}
              copy={{
                title: `${workerTypeLabel} の履歴書 / 職務経歴書をアップロード`,
                description:
                  'Automap Ingester が PDF を読み取り、人ごとに姓名・連絡先・職歴などを自動抽出します。複数人分をまとめてドロップできます。',
                dropzoneHint:
                  'ファイルをドラッグ＆ドロップ または クリックして選択',
                pickFileLabel: 'PDF をアップロード',
                pickFileDescription:
                  '履歴書・職務経歴書を 1 人あたり 1〜複数ファイル',
                sampleButtonLabel: 'サンプルデータを使う',
                sampleHint:
                  '実ファイルが手元にない場合はサンプルデータを使って一連のフローを確認できます。',
                nextButtonLabel: '次へ',
                extractingLabel: 'Automap が抽出中…',
                emptyHint: 'まだファイルが追加されていません',
                fileListHeading: 'アップロード済みファイル',
              }}
            />
          ))
          .with('review', () => (
            <ReviewStep
              copy={{
                title: '抽出結果を確認してください',
                description:
                  'Automap Ingester が各 PDF から抽出した内容です。信頼度が低い項目は赤枠で強調されています。必要に応じて修正してください。',
                backButtonLabel: 'アップロードに戻る',
                confirmButtonLabel: (count) =>
                  `この内容で ${count} 名を取り込む`,
              }}
              onBack={() => {
                // resetAll せず upload step に戻る場合は store の step だけ書き換える
                // 取り込みフロー継続のため一旦 resetAll しない方針 (再アップロード用)
                navigate({
                  to: '/person/automap-ingester/$workerType',
                  params: { workerType },
                });
              }}
            />
          ))
          .with('complete', () => (
            <CompletionStep
              copy={{
                title: (count) => `${count} 名をメンバー一覧に追加しました`,
                description: () =>
                  'Automap Ingester で取り込んだ全員を people に登録しました。詳細は workforce 一覧から確認できます。',
                primaryCtaLabel: 'メンバー一覧へ',
                secondaryCtaLabel: 'もう一度取り込む',
              }}
              onPrimary={() => {
                navigate({ to: '/people/workforce' });
              }}
            />
          ))
          .exhaustive()}
      </div>
    </HStack>
  );
}
